import {
  compose,
  system,
  ResponsiveValue,
  SpaceProps as OSpaceProps,
  LayoutProps as OLayoutProps,
  PositionProps as OPositionProps,
  ColorProps as OColorProps,
  TypographyProps as OTypographyProps,
  ShadowProps as OShadowProps,
  BorderProps as OBorderProps,
  FlexboxProps as OFlexboxProps,
} from "styled-system";
import {
  TextTransformProperty,
  VisibilityProperty,
  TextDecorationProperty,
  FloatProperty,
  ClearProperty,
} from "csstype";
import { CatchTheme } from "../theme";

export * from "styled-system";

type OverrideLast<A, B> = A & Omit<B, keyof A>;

// Creates a Prop type where Props override any existing props in the given native element props
export type OverrideNativeElementProps<
  Props,
  Tag extends keyof JSX.IntrinsicElements
> = OverrideLast<Props, JSX.IntrinsicElements[Tag]>;

export type SpaceProps = OSpaceProps<Required<CatchTheme>>;
export type LayoutProps = OLayoutProps<Required<CatchTheme>>;
export type PositionProps = OPositionProps<Required<CatchTheme>>;
export type ColorProps = OColorProps<Required<CatchTheme>>;
export type TypographyProps = OTypographyProps<Required<CatchTheme>>;
export type ShadowProps = OShadowProps<Required<CatchTheme>>;
export type BorderProps = OBorderProps<Required<CatchTheme>>;
export type FlexboxProps = OFlexboxProps<Required<CatchTheme>>;

export type VisibilityProps = {
  /** Should any text transform applys */
  visibility?: ResponsiveValue<VisibilityProperty>;
};

export const visibilityProp = system({
  visibility: {
    property: "visibility",
  },
});

export type TextTransformProp = {
  /** Should any text transform applys */
  textTransform?: ResponsiveValue<TextTransformProperty>;
};

export const textTransformProp = system({
  textTransform: {
    property: "textTransform",
  },
});

export type TextDecorationProp = {
  /** Should any text decoration applys */
  textDecoration?: TextDecorationProperty<number>;
};

export const textDecorationProp = system({
  textDecoration: {
    property: "textDecoration",
  },
});

export type HoverColorProp = {
  /** Color if hovered */
  hoverColor?: ResponsiveValue<string>;
};

export const hoverColorProp = system({
  hoverColor: {
    property: "color",
    scale: "colors",
  },
});

export type HoverBorderColorProp = {
  /** Border color if hover */
  hoverBorderColor?: ResponsiveValue<string>;
};

export const hoverBorderColorProp = system({
  hoverBorderColor: {
    property: "borderColor",
    scale: "colors",
  },
});

export type FocusBorderColorProp = {
  /** Border color if focused */
  focusBorderColor?: ResponsiveValue<string>;
};

export const focusBorderColorProp = system({
  focusBorderColor: {
    property: "borderColor",
    scale: "colors",
  },
});

export type FocusOutlineProp = {
  /** Css outline string if focused */
  focusOutline?: ResponsiveValue<string>;
};

export const focusOutlineProp = system({
  focusOutline: {
    property: "outline",
  },
});

export type PlaceholderTextProps = {
  /** Placeholder text font size */
  placeholderFontSize?: ResponsiveValue<string>;
  /** Placeholder text font weight */
  placeholderFontWeight?: ResponsiveValue<number | string>;
};

export const placeholderTextProps = system({
  placeholderFontSize: {
    property: "fontSize",
    scale: "fontSizes",
  },
  placeholderFontWeight: {
    property: "fontWeight",
    scale: "fontWeights",
  },
});

export type FloatProps = {
  float?: ResponsiveValue<FloatProperty>;
  clear?: ResponsiveValue<ClearProperty>;
};

export const float = compose(
  system({
    clear: {
      property: "clear",
    },
  }),
  system({
    float: {
      property: "float",
    },
  })
);

export type CursorProp = {
  cursor?: ResponsiveValue<
    | "auto"
    | "default"
    | "none"
    | "context-menu"
    | "help"
    | "pointer"
    | "progress"
    | "wait"
    | "cell"
    | "crosshair"
    | "text"
    | "vertical-text"
    | "alias"
    | "copy"
    | "move"
    | "no-drop"
    | "not-allowed"
    | "e-resize"
    | "n-resize"
    | "ne-resize"
    | "nw-resize"
    | "s-resize"
    | "se-resize"
    | "sw-resize"
    | "w-resize"
    | "ew-resize"
    | "ns-resize"
    | "nesw-resize"
    | "nwse-resize"
    | "col-resize"
    | "row-resize"
    | "all-scroll"
    | "zoom-in"
    | "zoom-out"
    | "grab"
    | "grabbing"
  >;
};
export const cursorProp = system({
  cursor: {
    property: "cursor",
  },
});
